<template>
    <div class="communities-listing-wrapper">
        <div v-if="loading" class="text-center py-5">
            <img class="d-none d-lg-block w-100" src="/wp-content/themes/understrap-child/reader-web-app-angular/assets/images/home-brands/Brands.png" alt="" srcset="">
            <img  class="d-none d-md-block d-lg-none" src="/wp-content/themes/understrap-child/reader-web-app-angular/assets/images/home-brands/Loading_screen_768px.svg" alt="" srcset="">
            <img  class="d-block d-md-none" src="/wp-content/themes/understrap-child/reader-web-app-angular/assets/images/home-brands/Loading_screen_414px.svg" alt="" srcset="">
        </div>
        
        <!-- Card Grid View -->
        <div v-if="!loading && !showDetailView && communities && communities.length" class="container-fluid">
            <div class="row">
                <div v-for="(community, index) in communities" :key="index" class="col-lg-3 col-md-4 col-sm-6 col-12 mb-3">
                    <div 
                        class="community-card h-100 position-relative overflow-hidden rounded-corners-10 cursorPointer"
                        :style="{ background: 'linear-gradient(' + community.primaryColourHexCode + ' , ' + community.secondaryColourHexCode + ')' }"
                        @click="selectCommunity(community)"
                        tabindex="0"
                        @keydown.enter="selectCommunity(community)"
                    >
                        <!-- Background image overlay -->
                        <div v-if="community.background && community.background.svg" 
                             class="community-bg-image"
                             :style="{ backgroundImage: `url(${community.background.svg})` }">
                        </div>
                        
                        <!-- Content -->
                        <div class="community-content p-3 position-relative">
                            <!-- Logo -->
                            <div class="community-logo mb-2">
                                <img v-if="community.logo && community.logo.svg" 
                                     :src="community.logo.svg" 
                                     :alt="community.name"
                                     class="w-100">
                            </div>
                            
                            <!-- Keywords/Tags -->
                            <div class="community-tags mt-auto">
                                <span v-for="(category, catIndex) in community.categories.slice(0, 3)" 
                                      :key="catIndex"
                                      class="tag badge badge-pill px-3 py-2 mr-2 mb-2">
                                    {{ category.name }}
                                </span>
                            </div>
                        </div>
                        
                        <!-- Hover indicator -->
                        <div class="hover-indicator">
                            <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Detail View Component -->
        <KeepAlive>
            <communityDetail 
                v-if="!loading && showDetailView && selectedCommunity"
                :key="selectedCommunity._id"
                :selectedCommunity="selectedCommunity"
                :communities="communities"
                :brandData="getBrandDataForCommunity(selectedCommunity)"
                :settingsPerm="settingsPerm"
                @switch-community="switchCommunity"
                @close="closeDetailView"
            />
        </KeepAlive>
    </div>
</template>

<script>
import {
    ApolloClient,
    InMemoryCache
} from '@apollo/client/core';
import { provideApolloClient, useQuery } from '@vue/apollo-composable';
import { watch } from 'vue';
import CONCORD_QUERIES from '../../services/concord_queries.js';
import communityDetail from './communityDetail.vue';

export default {
    name: 'communitiesListing',
    components: {
        communityDetail
    },
    props: {
        settingsPerm: Object
    },
    data() {
        return {
            loading: true,
            communities: [],
            showDetailView: false,
            selectedCommunity: null,
            isDevMioActive: this.settingsPerm.isDevMioActive,
            isDevmioNlActive: this.settingsPerm.isDevmioNlActive,
            brandSpecificData: (this.settingsPerm.brandsWidgetsData && this.settingsPerm.brandsWidgetsData.brandSpecificData) || null
        };
    },
    methods: {
        selectCommunity(community) {
            this.selectedCommunity = community;
            this.showDetailView = true;
        },
        switchCommunity(community) {
            this.selectedCommunity = community;
        },
        closeDetailView() {
            this.showDetailView = false;
            this.selectedCommunity = null;
        },
        getBrandDataForCommunity(community) {
            // brandSpecificData is structured as: { 'slug': { community_about, community_audience, newsletter_subscription } }
            if (!this.brandSpecificData || !community || !community.slug) {
                return {};
            }
            
            return this.brandSpecificData[community.slug] || {};
        }
    },
    created() {
        const cache = new InMemoryCache();
        const customHeaders = {
            platform: "web",
            App: this.isDevmioNlActive
            ? "devmionl"
            : this.isDevMioActive
            ? "devmio"
            : "entwickler"
        };
        const apolloClient = new ApolloClient({
            cache,
            uri: this.settingsPerm.concordBackendURL,
            headers: customHeaders,
        });
        
        provideApolloClient(apolloClient);

        // Fetch brand complexes (communities)
        const { result } = useQuery(CONCORD_QUERIES.ALL_BRAND_COMPLEXES);
        watch(result, (value) => {
            if (value && value.brandComplexes && value.brandComplexes.BrandComplexes) {
                this.communities = value.brandComplexes.BrandComplexes;
            }
            this.loading = false;
        });
    },
    mounted() {
        // Add escape key listener
        window.addEventListener('keydown', this.handleEscKey);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleEscKey);
    }
};
</script>

<style scoped>
.communities-listing-wrapper {
    padding: 2rem 0;
}

.cursorPointer {
    cursor: pointer;
}

/* Card Styles */
.community-card {
    max-height: 230px;
    min-height: 230px;
    display: flex;
    flex-direction: column;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.community-card:hover,
.community-card:focus {
    transform: translateY(-8px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
    outline: none;
}

.community-card:focus {
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.5);
}

.community-card:hover .hover-indicator {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
}

.community-bg-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 1;
}

.community-content {
    z-index: 2;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.community-logo {
    max-width: 200px;
}

.community-logo img {
    filter: brightness(0) invert(1);
    height: 90px;
    width: auto;
    object-fit: contain;
}

.community-tags {
    display: flex;
    flex-wrap: wrap;
    margin-top: auto;
}

.tag {
    background-color: #F2F2F2BF;
    color: black;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.hover-indicator {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.8);
    background-color: rgba(255, 255, 255, 0.9);
    color: #003D72;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: all 0.3s ease;
    z-index: 3;
}

/* Responsive */
@media (max-width: 768px) {
    .community-card {
        min-height: 220px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .community-card,
    .hover-indicator {
        transition: none;
        animation: none;
    }
}
</style>

